import type { StatusTone } from '@/components/shell/StatusChip';
import type { Gateway, GatewayAccountMode, GatewayAccountStatus, GatewayFieldRequirement } from '@/api/models';

/** Maps a gateway account's status to one of the theme's five badge tones (foundations.md §7.8). */
export function gatewayAccountStatusTone(status: GatewayAccountStatus): StatusTone {
  return status === 'active' ? 'success' : 'warning';
}

/** `live` gets the info tone (it moves real money) — `test` stays neutral. */
export function gatewayAccountModeTone(mode: GatewayAccountMode): StatusTone {
  return mode === 'live' ? 'info' : 'neutral';
}

/**
 * Whether an account's external-identifier input belongs on screen for this
 * gateway. A gateway that never reads the field should not ask for it — but a
 * value already stored in one stays visible, so it can still be cleared rather
 * than becoming invisible and unremovable.
 *
 * With no descriptor (an API older than `GET /v1/admin/gateways`) every field
 * stays visible: the console degrades to its unguided shape instead of hiding
 * inputs it cannot reason about.
 */
export function isAccountFieldVisible(requirement: GatewayFieldRequirement | undefined, currentValue: string): boolean {
  if (requirement === undefined) return true;
  return requirement !== 'not_applicable' || currentValue.trim().length > 0;
}

/**
 * The gateway's own name for one of those fields ("merchant code", "terminal"),
 * falling back to the generic label when this console has no gateway-specific
 * wording for it.
 */
export function accountFieldLabel(
  translate: (key: string, options?: Record<string, unknown>) => string,
  gateway: Gateway,
  field: 'externalAccountCode' | 'externalTerminalId' | 'externalEndpointPrefix',
  genericLabel: string,
): string {
  return translate(`pay.gatewayAccounts.accountFields.${gateway}.${field}`, { _: genericLabel });
}
